import { create } from "zustand";
import type {
  Card,
  ChatSession,
  PendingMessage,
  ToolCall,
  ToolResultInfo,
  ConfirmationRequest,
} from "./types";

interface ChatState {
  /** Whether the chat drawer is open (full panel). */
  isOpen: boolean;
  /** Whether the chat is minimized to the mini bar. */
  isMinimized: boolean;
  /** Whether the history sidebar is open. */
  sidebarOpen: boolean;
  /** Currently active session (null = show welcome). */
  activeSession: ChatSession | null;
  /** Messages for the active session (server + pending). */
  messages: PendingMessage[];
  /** True while the assistant is streaming a response. */
  isStreaming: boolean;
  /** Error message to display, if any. */
  error: string | null;
  /** Cached session list from backend. */
  sessions: ChatSession[];
  /** Latest assistant message preview (for mini bar). */
  lastPreview: string;
  /** Unread flag — set when a message arrives while minimized. */
  hasUnread: boolean;
  /** Current streaming phase for tool-use flow. */
  streamingPhase: "idle" | "streaming" | "awaiting_confirmation";
  /** Tool calls in progress for the current response. */
  toolCalls: ToolCall[];
  /** Completed tool results for the current response. */
  toolResults: ToolResultInfo[];
  /** Rich cards emitted by tool results for the current response. */
  cards: Card[];
  /** Pending confirmation request, if any. */
  confirmationRequest: ConfirmationRequest | null;
  /** Tool names that have been confirmed this turn. */
  confirmedTools: string[];

  // Actions
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  minimize: () => void;
  restore: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setActiveSession: (session: ChatSession | null) => void;
  setMessages: (messages: PendingMessage[]) => void;
  addMessage: (message: PendingMessage) => void;
  appendToLastMessage: (text: string) => void;
  setStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
  setSessions: (sessions: ChatSession[]) => void;
  setStreamingPhase: (phase: "idle" | "streaming" | "awaiting_confirmation") => void;
  addToolCall: (call: ToolCall) => void;
  addToolResult: (result: ToolResultInfo) => void;
  addCard: (card: Card) => void;
  setConfirmationRequest: (req: ConfirmationRequest | null) => void;
  addConfirmedTool: (toolName: string) => void;
  clearToolState: () => void;
  reset: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  isOpen: false,
  isMinimized: false,
  sidebarOpen: false,
  activeSession: null,
  messages: [],
  isStreaming: false,
  error: null,
  sessions: [],
  lastPreview: "",
  hasUnread: false,
  streamingPhase: "idle",
  toolCalls: [],
  toolResults: [],
  cards: [],
  confirmationRequest: null,
  confirmedTools: [],

  openChat: () => set({ isOpen: true, isMinimized: false, hasUnread: false }),
  closeChat: () => set({ isOpen: false, isMinimized: false }),
  toggleChat: () =>
    set((s) => {
      if (s.isMinimized) return { isOpen: true, isMinimized: false, hasUnread: false };
      return { isOpen: !s.isOpen };
    }),

  minimize: () =>
    set((s) => ({
      isOpen: false,
      isMinimized: true,
      lastPreview: s.messages.at(-1)?.content.slice(0, 80) ?? "",
    })),
  restore: () => set({ isOpen: true, isMinimized: false, hasUnread: false }),

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  setActiveSession: (session) =>
    set({ activeSession: session, messages: [], error: null }),

  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((s) => ({
      messages: [...s.messages, message],
      error: null,
      hasUnread: s.isMinimized && message.role === "assistant" ? true : s.hasUnread,
    })),

  appendToLastMessage: (text) =>
    set((s) => {
      const msgs = [...s.messages];
      const last = msgs[msgs.length - 1];
      if (last && last.role === "assistant") {
        msgs[msgs.length - 1] = { ...last, content: last.content + text };
      }
      return { messages: msgs };
    }),

  setStreaming: (streaming) => set({ isStreaming: streaming }),
  setError: (error) => set({ error }),
  setSessions: (sessions) => set({ sessions }),

  setStreamingPhase: (phase) => set({ streamingPhase: phase }),
  addToolCall: (call) => set((s) => ({ toolCalls: [...s.toolCalls, call] })),
  addToolResult: (result) => set((s) => ({ toolResults: [...s.toolResults, result] })),
  addCard: (card) => set((s) => ({ cards: [...s.cards, card] })),
  setConfirmationRequest: (req) => set({ confirmationRequest: req }),
  addConfirmedTool: (toolName) => set((s) => ({ confirmedTools: [...s.confirmedTools, toolName] })),
  clearToolState: () => set({ toolCalls: [], toolResults: [], cards: [], confirmationRequest: null }),

  reset: () =>
    set({
      activeSession: null,
      messages: [],
      isStreaming: false,
      error: null,
      sidebarOpen: false,
      streamingPhase: "idle",
      toolCalls: [],
      toolResults: [],
      cards: [],
      confirmationRequest: null,
      confirmedTools: [],
    }),
}));
